import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Play, ChevronRight, Clock } from 'lucide-react';
import { getRandomSoalNNC2, getRandomSoalGambar, getRandomSoalTeks, getRandomSoalCampuran, getSoalHitungI, soalNNC2, soalGambar, soalTeks } from './bankSoalKecermatan';
import { catatHasilTes } from './progressTracker';
import { useCegahKeluarTes } from './cegahKeluarTes';
import { bacaProgresTes, hapusProgresTes, useAutosaveTes } from './useResumeTes';
import { modulBaruAktif } from './modulBaruConfig';

const SEMUA_BANK_KECERMATAN = [...soalNNC2, ...soalGambar, ...soalTeks];
function soalDariMetaKecermatan(meta) {
  const byId = {};
  SEMUA_BANK_KECERMATAN.forEach(s => { byId[s.id] = s; });
  // Soal Hitung Huruf di-generate on-the-fly (bukan bank statis) — kalau resume
  // dari progres tersimpan, deret & jumlahI-nya sudah ikut disimpan di meta itu
  // sendiri (lihat useAutosaveTes di bawah), jadi ambil langsung dari situ.
  return (meta || []).map(m => {
    if (m.jenis === 'hitungHuruf') return { id: m.id, deret: m.deret, jumlahI: m.jumlahI, jenis: 'hitungHuruf' };
    return byId[m.id] ? { ...byId[m.id], jenis: m.jenis } : null;
  }).filter(Boolean);
}

const WAKTU_PER_SOAL_NNC2 = 15;
const TOTAL_SOAL_NNC2 = 30;
const WAKTU_PER_SOAL_GAMBAR = 10;
const TOTAL_SOAL_GAMBAR = 20;
const WAKTU_PER_SOAL_TEKS = 10;
const TOTAL_SOAL_TEKS = 20;
const WAKTU_PER_SOAL_HITUNGI = 10;
const TOTAL_SOAL_HITUNGI = 20;
const JUMLAH_CAMPURAN_DASAR = { nnc2: 15, gambar: 10, teks: 10 };
// Hitung Huruf ikut masuk komposisi resmi Tryout HANYA mulai tryout yang
// tanggal_mulai-nya >= tanggal di modulBaruConfig.js (lihat prop tryoutTanggalMulai).
const JUMLAH_HITUNGI_CAMPURAN = 10;

function jumlahCampuran(hitungIAktif) {
  return hitungIAktif ? { ...JUMLAH_CAMPURAN_DASAR, hitungHuruf: JUMLAH_HITUNGI_CAMPURAN } : JUMLAH_CAMPURAN_DASAR;
}
function totalSoalCampuran(hitungIAktif) {
  const j = jumlahCampuran(hitungIAktif);
  return j.nnc2 + j.gambar + j.teks + (j.hitungHuruf || 0);
}

function buatModes(hitungIAktif) {
  return {
    nnc2: {
      label: 'NNC-2',
      subtitle: 'Perbandingan Tiga Kolom',
      totalSoal: TOTAL_SOAL_NNC2,
      waktuPerSoal: WAKTU_PER_SOAL_NNC2,
      modulId: 'kecermatan',
    },
    gambar: {
      label: 'Gambar',
      subtitle: 'Banding Dua Gambar Sama/Beda',
      totalSoal: TOTAL_SOAL_GAMBAR,
      waktuPerSoal: WAKTU_PER_SOAL_GAMBAR,
      modulId: 'kecermatan',
    },
    teks: {
      label: 'Teks',
      subtitle: 'Banding Dua Rangkaian Teks Sama/Beda',
      totalSoal: TOTAL_SOAL_TEKS,
      waktuPerSoal: WAKTU_PER_SOAL_TEKS,
      modulId: 'kecermatan',
    },
    hitungHuruf: {
      label: 'Hitung Huruf',
      subtitle: 'Hitung Kemunculan Huruf "i" di Antara Angka',
      totalSoal: TOTAL_SOAL_HITUNGI,
      waktuPerSoal: WAKTU_PER_SOAL_HITUNGI,
      modulId: 'kecermatan',
    },
    campuran: {
      label: 'Campuran',
      subtitle: hitungIAktif ? 'NNC-2 + Gambar + Teks + Hitung Huruf (komposisi resmi Tryout)' : 'NNC-2 + Gambar + Teks (komposisi resmi Tryout)',
      totalSoal: totalSoalCampuran(hitungIAktif),
      waktuPerSoal: null, // bervariasi per jenis soal, lihat waktuUntukSoal()
      modulId: 'kecermatan',
    },
  };
}

function waktuUntukSoal(soal, mode, MODES) {
  if (mode !== 'campuran') return MODES[mode].waktuPerSoal;
  if (soal?.jenis === 'nnc2') return WAKTU_PER_SOAL_NNC2;
  if (soal?.jenis === 'hitungHuruf') return WAKTU_PER_SOAL_HITUNGI;
  return WAKTU_PER_SOAL_GAMBAR;
}

function HasilScreen({ mode, hasil, total, waktuTotal, onReset, onBack, tryoutMode, tryoutId, MODES }) {
  const pct = Math.round((hasil.benar / total) * 100);
  const kategori = pct >= 80 ? 'Sangat Baik' : pct >= 65 ? 'Baik' : pct >= 50 ? 'Cukup' : 'Perlu Latihan';
  const warna = pct >= 80 ? '#4ade80' : pct >= 65 ? '#C0A062' : pct >= 50 ? '#60a5fa' : '#f87171';

  useEffect(() => {
    catatHasilTes('kecermatan', pct, { mode, benar: hasil.benar, salah: hasil.salah, kosong: hasil.kosong, total }, tryoutMode, tryoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#171C14] text-[#F4F1E6] px-5 py-6 overflow-y-auto">

      <button onClick={onBack} className="flex items-center gap-2 text-stone-400 text-sm mb-6">
        <ArrowLeft size={16} /> Kembali ke Menu
      </button>

      <p className="text-[#C0A062] text-xs uppercase tracking-widest mb-1">Hasil Tes</p>
      <h1 style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700 }} className="text-3xl mb-6">
        KECERMATAN {MODES[mode].label.toUpperCase()}
      </h1>

      <div className="bg-[#232A1C] border border-[#3A4530] rounded-xl p-6 mb-4 text-center">
        <p className="text-stone-400 text-sm mb-2">Skor Kamu</p>
        <p style={{ fontFamily: "'JetBrains Mono', monospace", color: warna }} className="text-6xl font-bold mb-1">
          {pct}
        </p>
        <p className="text-stone-500 text-xs mb-3">dari 100</p>
        <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ background: warna + '22', color: warna, border: `1px solid ${warna}44` }}>
          {kategori}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          { label: 'Benar', val: hasil.benar, color: '#4ade80' },
          { label: 'Salah', val: hasil.salah, color: '#f87171' },
          { label: 'Kosong', val: hasil.kosong, color: '#888780' },
        ].map(item => (
          <div key={item.label} className="bg-[#232A1C] border border-[#3A4530] rounded-xl p-4 text-center">
            <p style={{ fontFamily: "'JetBrains Mono', monospace", color: item.color }} className="text-2xl font-bold">
              {item.val}
            </p>
            <p className="text-stone-500 text-[11px] uppercase tracking-wide mt-1">{item.label}</p>
          </div>
        ))}
      </div>

      <div className="bg-[#232A1C] border border-[#3A4530] rounded-xl p-4 mb-6 flex items-center justify-between">
        <span className="text-stone-400 text-sm">Waktu pengerjaan</span>
        <span style={{ fontFamily: "'JetBrains Mono', monospace" }} className="text-[#C0A062]">
          {Math.floor(waktuTotal / 60)}m {waktuTotal % 60}s
        </span>
      </div>

      <div className="bg-[#232A1C] border border-[#3A4530] rounded-xl p-4 mb-6">
        <p style={{ fontFamily: "'Poppins', sans-serif" }} className="text-[#C0A062] text-xs font-semibold uppercase tracking-wider mb-3">
          Analisis
        </p>
        <p className="text-stone-300 text-sm leading-relaxed">
          {pct >= 80
            ? 'Tingkat ketelitian kamu sangat tinggi. Kemampuan membandingkan data secara cepat dan akurat sudah sangat baik.'
            : pct >= 65
            ? 'Ketelitian kamu cukup baik. Perhatikan detail-detail kecil yang mudah terlewat saat terburu-buru.'
            : pct >= 50
            ? 'Masih perlu latihan lebih intensif. Coba perlambat sedikit dan perhatikan setiap detail dengan teliti.'
            : 'Latihan rutin sangat dianjurkan. Fokus pada ketelitian dulu, kecepatan akan mengikuti seiring latihan.'}
        </p>
      </div>

      <div className="space-y-3">
        <button
          onClick={onReset}
          className="w-full bg-[#C0A062] text-[#171C14] font-bold py-4 rounded-xl uppercase tracking-widest"
          style={{ fontFamily: "'Poppins', sans-serif" }}
        >
          Ulangi Tes
        </button>
        <button
          onClick={onBack}
          className="w-full border border-[#3A4530] text-stone-400 py-3.5 rounded-xl text-sm"
        >
          Kembali ke Menu
        </button>
      </div>
    </div>
  );
}

function totalDetikCampuran(hitungIAktif) {
  const j = jumlahCampuran(hitungIAktif);
  return j.nnc2 * WAKTU_PER_SOAL_NNC2 + (j.gambar + j.teks) * WAKTU_PER_SOAL_GAMBAR + (j.hitungHuruf || 0) * WAKTU_PER_SOAL_HITUNGI;
}

function CaraMenjawab({ mode, hitungIAktif }) {
  if (mode === 'nnc2') {
    return (
      <div className="bg-[#2E3826]/60 border border-[#3A4530] rounded-xl p-4 mb-6">
        <p className="text-[#C0A062] text-xs font-semibold uppercase tracking-wider mb-3">Cara Menjawab</p>
        <div className="space-y-2 text-stone-300 text-sm">
          {[
            ['a', 'Kolom 1, 2, dan 3 persis sama'],
            ['b', 'Hanya kolom 1 dan 2 yang sama'],
            ['c', 'Hanya kolom 1 dan 3 yang sama'],
            ['d', 'Hanya kolom 2 dan 3 yang sama'],
            ['e', 'Tidak ada yang sama'],
          ].map(([huruf, keterangan]) => (
            <div key={huruf} className="flex items-start gap-3">
              <span className="w-6 h-6 rounded bg-[#3A4530] flex items-center justify-center text-[#C0A062] text-xs font-bold flex-shrink-0 mt-0.5">
                {huruf}
              </span>
              <span>{keterangan}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (mode === 'teks') {
    return (
      <div className="bg-[#2E3826]/60 border border-[#3A4530] rounded-xl p-4 mb-6">
        <p className="text-[#C0A062] text-xs font-semibold uppercase tracking-wider mb-3">Cara Menjawab</p>
        <p className="text-stone-300 text-sm leading-relaxed">
          Dua rangkaian huruf/angka/kata akan ditampilkan berdampingan. Tandai <span className="text-[#C0A062] font-semibold">SAMA</span> kalau keduanya identik persis karakter demi karakter, atau <span className="text-[#C0A062] font-semibold">BEDA</span> kalau ada perbedaan sekecil apapun (huruf, angka, ejaan, atau tanda baca).
        </p>
      </div>
    );
  }
  if (mode === 'hitungHuruf') {
    return (
      <div className="bg-[#2E3826]/60 border border-[#3A4530] rounded-xl p-4 mb-6">
        <p className="text-[#C0A062] text-xs font-semibold uppercase tracking-wider mb-3">Cara Menjawab</p>
        <p className="text-stone-300 text-sm leading-relaxed">
          Satu baris deretan angka akan ditampilkan, dengan huruf <span className="text-[#C0A062] font-semibold">"i"</span> diselipkan di antaranya. Hitung berapa kali huruf "i" muncul pada baris itu, lalu ketuk angka yang sesuai.
        </p>
      </div>
    );
  }
  if (mode === 'campuran') {
    const j = jumlahCampuran(hitungIAktif);
    return (
      <div className="bg-[#2E3826]/60 border border-[#3A4530] rounded-xl p-4 mb-6">
        <p className="text-[#C0A062] text-xs font-semibold uppercase tracking-wider mb-3">Cara Menjawab</p>
        <p className="text-stone-300 text-sm leading-relaxed">
          Soal berjalan berurutan dalam {j.hitungHuruf ? '4' : '3'} bagian: {j.nnc2} soal <span className="text-[#C0A062] font-semibold">NNC-2</span> (pilih a/b/c/d/e), {j.gambar} soal <span className="text-[#C0A062] font-semibold">Gambar</span>, {j.teks} soal <span className="text-[#C0A062] font-semibold">Teks</span> (keduanya tandai SAMA/BEDA){j.hitungHuruf ? <>, dan {j.hitungHuruf} soal <span className="text-[#C0A062] font-semibold">Hitung Huruf</span> (ketuk jumlah huruf "i")</> : ''}. Waktu per soal otomatis menyesuaikan jenisnya.
        </p>
      </div>
    );
  }
  return (
    <div className="bg-[#2E3826]/60 border border-[#3A4530] rounded-xl p-4 mb-6">
      <p className="text-[#C0A062] text-xs font-semibold uppercase tracking-wider mb-3">Cara Menjawab</p>
      <p className="text-stone-300 text-sm leading-relaxed">
        Dua gambar akan ditampilkan berdampingan. Tandai <span className="text-[#C0A062] font-semibold">SAMA</span> kalau keduanya identik persis, atau <span className="text-[#C0A062] font-semibold">BEDA</span> kalau ada perbedaan sekecil apapun (bentuk, arah, jumlah, atau posisi).
      </p>
    </div>
  );
}

function BannerLanjutkanKecermatan({ progresTersimpan, onLanjutkan, onMulaiBaru, MODES }) {
  if (!progresTersimpan) return null;
  const labelMode = MODES[progresTersimpan.mode]?.label || progresTersimpan.mode;
  const totalSoal = progresTersimpan.soalMeta?.length || 0;
  const m = Math.floor((progresTersimpan.waktuTotal || 0) / 60);
  return (
    <div className="rounded-xl p-4 mb-5 border-2" style={{ borderColor: '#60A5FA', backgroundColor: '#60A5FA22' }}>
      <p className="text-xs font-bold uppercase tracking-wide mb-1" style={{ color: '#93C5FD' }}>⏸️ Ada Pengerjaan Sebelumnya</p>
      <p className="text-sm text-stone-200 mb-3">
        Mode <b>{labelMode}</b>, baru sampai soal <b>{(progresTersimpan.currentIdx || 0) + 1}/{totalSoal}</b> (sudah berjalan {m} menit).
      </p>
      <div className="flex gap-2">
        <button onClick={onLanjutkan} className="flex-1 py-2 rounded-lg text-sm font-bold" style={{ backgroundColor: '#60A5FA', color: '#171C14' }}>
          Lanjutkan
        </button>
        <button onClick={onMulaiBaru} className="flex-1 py-2 rounded-lg border text-sm font-semibold text-stone-300" style={{ borderColor: '#60A5FA' }}>
          Mulai Baru
        </button>
      </div>
    </div>
  );
}

function LayarPilihMode({ mode, setMode, onBack, onMulai, tryoutMode, progresTersimpan, onLanjutkan, onMulaiBaru, MODES, hitungIAktif }) {
  const waktuTotalDetik = mode === 'campuran' ? totalDetikCampuran(hitungIAktif) : MODES[mode].totalSoal * MODES[mode].waktuPerSoal;

  // Konteks Tryout resmi: mode dikunci ke Campuran, tidak ada pilihan lain —
  // semua peserta mengerjakan komposisi soal yang sama (mirip pola LogikaModul).
  if (tryoutMode) {
    return (
      <div className="min-h-screen bg-[#171C14] text-[#F4F1E6] px-5 py-6 flex flex-col">
        <div className="flex-1">
          <p className="text-[#C0A062] text-xs uppercase tracking-widest mb-2">Tes Kecermatan</p>
          <h1 style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700 }} className="text-4xl mb-1">Campuran</h1>
          <h2 style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 500 }} className="text-xl text-stone-400 mb-6">Bagian dari Tryout Resmi</h2>

          <BannerLanjutkanKecermatan progresTersimpan={progresTersimpan} onLanjutkan={onLanjutkan} onMulaiBaru={onMulaiBaru} MODES={MODES} />

          <div className="rounded-xl p-4 mb-5 border-2" style={{ borderColor: 'var(--accent)', backgroundColor: 'var(--bg-card2)' }}>
            <p className="text-[#C0A062] text-xs font-bold uppercase tracking-wide mb-1">📌 Perhatian</p>
            <p className="text-sm font-semibold text-stone-200 leading-relaxed">
              Karena ini bagian dari Tryout resmi, mode dikunci otomatis ke Campuran ({hitungIAktif ? 'NNC-2 + Gambar + Teks + Hitung Huruf' : 'NNC-2 + Gambar + Teks'}) — semua peserta mengerjakan komposisi soal yang sama.
            </p>
          </div>

          <div className="bg-[#232A1C] border border-[#3A4530] rounded-xl p-5 mb-5 space-y-3">
            {[
              ['Jumlah Soal', `${MODES.campuran.totalSoal} soal`],
              ['Total Waktu', `±${Math.round(waktuTotalDetik / 60)} menit`],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between">
                <span className="text-stone-400 text-sm">{k}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace" }} className="text-[#C0A062] text-sm font-medium">{v}</span>
              </div>
            ))}
          </div>

          <CaraMenjawab mode="campuran" hitungIAktif={hitungIAktif} />
        </div>

        <button
          onClick={onMulai}
          className="w-full bg-[#C0A062] text-[#171C14] font-bold py-4 rounded-xl uppercase tracking-widest flex items-center justify-center gap-2"
          style={{ fontFamily: "'Poppins', sans-serif" }}
        >
          <Play size={18} /> Mulai Tes
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#171C14] text-[#F4F1E6] px-5 py-6 flex flex-col">
      <button onClick={onBack} className="flex items-center gap-2 text-stone-400 text-sm mb-8">
        <ArrowLeft size={16} /> Kembali
      </button>

      <div className="flex-1">
        <p className="text-[#C0A062] text-xs uppercase tracking-widest mb-2">Tes Kecermatan</p>
        <h1 style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700 }} className="text-4xl mb-1">
          {MODES[mode].label}
        </h1>
        <h2 style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 500 }} className="text-xl text-stone-400 mb-6">
          {MODES[mode].subtitle}
        </h2>

        <BannerLanjutkanKecermatan progresTersimpan={progresTersimpan} onLanjutkan={onLanjutkan} onMulaiBaru={onMulaiBaru} MODES={MODES} />

        {/* Pilih mode */}
        <div className="grid grid-cols-2 gap-2 mb-5">
          {Object.entries(MODES).map(([key, m]) => (
            <button
              key={key}
              onClick={() => setMode(key)}
              className={`rounded-xl p-3 text-left border transition
                ${mode === key ? 'border-[#C0A062] bg-[#2E3826]' : 'border-[#3A4530] bg-[#232A1C]'}`}
            >
              <p className={`font-bold text-sm ${mode === key ? 'text-[#C0A062]' : 'text-stone-300'}`}>{m.label}</p>
              <p className="text-stone-500 text-[10px] mt-0.5 leading-snug">{m.subtitle}</p>
            </button>
          ))}
        </div>

        <div className="bg-[#232A1C] border border-[#3A4530] rounded-xl p-5 mb-5 space-y-3">
          {[
            ['Jumlah Soal', `${MODES[mode].totalSoal} soal`],
            ['Total Waktu', `±${Math.round(waktuTotalDetik / 60)} menit`],
            ['Format Jawaban', mode === 'nnc2' ? 'Pilih a / b / c / d / e' : mode === 'campuran' ? 'Campuran a-e, SAMA/BEDA & angka' : mode === 'hitungHuruf' ? 'Ketuk angka jumlah huruf "i"' : 'Pilih SAMA / BEDA'],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between">
              <span className="text-stone-400 text-sm">{k}</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace" }} className="text-[#C0A062] text-sm font-medium">{v}</span>
            </div>
          ))}
        </div>

        <CaraMenjawab mode={mode} hitungIAktif={hitungIAktif} />
      </div>

      <button
        onClick={onMulai}
        className="w-full bg-[#C0A062] text-[#171C14] font-bold py-4 rounded-xl uppercase tracking-widest flex items-center justify-center gap-2"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <Play size={18} /> Mulai Tes
      </button>
    </div>
  );
}

export default function KecermatanModul({ onBack, tryoutMode = false, tryoutId = null, tryoutTanggalMulai = null }) {
  const [phase, setPhase] = useState('intro');
  useCegahKeluarTes(phase === 'tes', onBack);
  const hitungIAktif = modulBaruAktif(tryoutTanggalMulai);
  const MODES = buatModes(hitungIAktif);
  const [mode, setMode] = useState(tryoutMode ? 'campuran' : 'nnc2');
  const [soalList, setSoalList] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [jawaban, setJawaban] = useState([]);
  const [waktuSoal, setWaktuSoal] = useState(WAKTU_PER_SOAL_NNC2);
  const [waktuTotal, setWaktuTotal] = useState(0);
  const [hasil, setHasil] = useState(null);
  const timerRef = useRef(null);
  const totalTimerRef = useRef(null);
  const [progresTersimpan, setProgresTersimpan] = useState(null);

  const totalSoal = MODES[mode].totalSoal;

  useEffect(() => {
    setProgresTersimpan(bacaProgresTes('kecermatan', tryoutId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useAutosaveTes(phase === 'tes', 'kecermatan', tryoutId, {
    mode,
    // Soal Hitung Huruf di-generate on-the-fly (bukan bank statis) — simpan
    // deret & jumlahI-nya langsung di meta supaya resume bisa merekonstruksi
    // persis soal yang sama, bukan bank lookup by id seperti jenis lain.
    soalMeta: soalList.map(s => s.jenis === 'hitungHuruf'
      ? { id: s.id, jenis: s.jenis, deret: s.deret, jumlahI: s.jumlahI }
      : { id: s.id, jenis: s.jenis }),
    currentIdx,
    jawaban,
    waktuTotal,
  });

  const startTes = () => {
    hapusProgresTes('kecermatan', tryoutId);
    const jCampuran = jumlahCampuran(hitungIAktif);
    const soal = mode === 'nnc2' ? getRandomSoalNNC2(totalSoal)
      : mode === 'teks' ? getRandomSoalTeks(totalSoal)
      : mode === 'hitungHuruf' ? getSoalHitungI(totalSoal).map(s => ({ ...s, jenis: 'hitungHuruf' }))
      : mode === 'campuran' ? getRandomSoalCampuran({ jumlahNNC2: jCampuran.nnc2, jumlahGambar: jCampuran.gambar, jumlahTeks: jCampuran.teks, jumlahHitungHuruf: jCampuran.hitungHuruf || 0 })
      : getRandomSoalGambar(totalSoal);
    setSoalList(soal);
    setCurrentIdx(0);
    setJawaban([]);
    setWaktuSoal(waktuUntukSoal(soal[0], mode, MODES));
    setWaktuTotal(0);
    setProgresTersimpan(null);
    setPhase('tes');
  };

  const handleLanjutkan = () => {
    if (!progresTersimpan) return;
    const soal = soalDariMetaKecermatan(progresTersimpan.soalMeta);
    if (soal.length === 0) { setProgresTersimpan(null); return; }
    setMode(progresTersimpan.mode);
    setSoalList(soal);
    const idx = Math.min(progresTersimpan.currentIdx || 0, soal.length - 1);
    setCurrentIdx(idx);
    setJawaban(progresTersimpan.jawaban || []);
    setWaktuSoal(waktuUntukSoal(soal[idx], progresTersimpan.mode, MODES));
    setWaktuTotal(progresTersimpan.waktuTotal || 0);
    setPhase('tes');
  };

  const handleMulaiBaruDariBanner = () => {
    hapusProgresTes('kecermatan', tryoutId);
    setProgresTersimpan(null);
  };

  useEffect(() => {
    if (phase === 'tes') {
      totalTimerRef.current = setInterval(() => setWaktuTotal(t => t + 1), 1000);
      return () => clearInterval(totalTimerRef.current);
    }
  }, [phase]);

  useEffect(() => {
    if (phase !== 'tes') return;
    const waktuIni = waktuUntukSoal(soalList[currentIdx], mode, MODES);
    setWaktuSoal(waktuIni);
    timerRef.current = setInterval(() => {
      setWaktuSoal(t => {
        if (t <= 1) {
          handleJawab(null);
          return waktuIni;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIdx, phase]);

  // Soal Hitung Huruf pakai field `jumlahI` (angka) sebagai kunci jawaban,
  // bukan `jawaban` (huruf/kata) seperti jenis soal Kecermatan lainnya.
  const kunciJawabanSoal = (soal) => soal?.jenis === 'hitungHuruf' ? soal.jumlahI : soal?.jawaban;

  const handleJawab = (pilihan) => {
    clearInterval(timerRef.current);
    const newJawaban = [...jawaban, pilihan];
    setJawaban(newJawaban);

    if (currentIdx + 1 >= soalList.length) {
      clearInterval(totalTimerRef.current);
      hapusProgresTes('kecermatan', tryoutId);
      const benar = newJawaban.filter((j, i) => j !== null && j === kunciJawabanSoal(soalList[i])).length;
      const salah = newJawaban.filter((j, i) => j !== null && j !== kunciJawabanSoal(soalList[i])).length;
      const kosong = newJawaban.filter(j => j === null).length;
      setHasil({ benar, salah, kosong });
      setPhase('hasil');
    } else {
      setCurrentIdx(i => i + 1);
    }
  };

  const handleReset = () => {
    setPhase('intro');
    setHasil(null);
    setJawaban([]);
    setCurrentIdx(0);
  };

  if (phase === 'hasil') {
    return <HasilScreen mode={mode} hasil={hasil} total={totalSoal} waktuTotal={waktuTotal} onReset={handleReset} onBack={onBack} tryoutMode={tryoutMode} tryoutId={tryoutId} MODES={MODES} />;
  }

  if (phase === 'intro') {
    return (
      <LayarPilihMode
        mode={mode}
        setMode={setMode}
        onBack={onBack}
        onMulai={startTes}
        tryoutMode={tryoutMode}
        progresTersimpan={progresTersimpan}
        onLanjutkan={handleLanjutkan}
        onMulaiBaru={handleMulaiBaruDariBanner}
        MODES={MODES}
        hitungIAktif={hitungIAktif}
      />
    );
  }

  // PHASE: tes
  const soal = soalList[currentIdx];
  const jenisTampil = mode === 'campuran' ? soal?.jenis : mode;
  const progress = ((currentIdx) / totalSoal) * 100;
  const timerPct = (waktuSoal / waktuUntukSoal(soal, mode, MODES)) * 100;

  return (
    <div className="min-h-screen bg-[#171C14] text-[#F4F1E6] flex flex-col">

      {/* Header */}
      <div className="px-4 pt-4 pb-3 bg-[#1B2017] border-b border-[#3A4530] flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <p style={{ fontFamily: "'JetBrains Mono', monospace" }} className="text-[#C0A062] text-sm font-bold">
            {currentIdx + 1} <span className="text-stone-600">/ {totalSoal}</span>
          </p>
          <div className="flex items-center gap-2">
            <Clock size={14} className={waktuSoal <= 5 ? 'text-[#f87171]' : 'text-stone-400'} />
            <p style={{ fontFamily: "'JetBrains Mono', monospace" }}
              className={`text-xl font-bold ${waktuSoal <= 5 ? 'text-[#f87171]' : 'text-[#F4F1E6]'}`}>
              {waktuSoal}
            </p>
          </div>
        </div>
        <div className="h-1.5 bg-[#2E3826] rounded-full overflow-hidden mb-1">
          <div className="h-full bg-[#C0A062] rounded-full transition-all" style={{ width: `${progress}%` }} />
        </div>
        <div className="h-1 bg-[#2E3826] rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${waktuSoal <= 5 ? 'bg-[#f87171]' : 'bg-[#3A4530]'}`}
            style={{ width: `${timerPct}%` }}
          />
        </div>
      </div>

      {jenisTampil === 'nnc2' ? (
        <div className="flex-1 px-4 py-6 flex flex-col justify-center">
          <p className="text-stone-500 text-xs uppercase tracking-wider mb-4 text-center">
            Bandingkan tiga kolom berikut
          </p>

          <div className="bg-[#232A1C] border border-[#3A4530] rounded-xl p-5 mb-6">
            <div className="grid grid-cols-3 gap-2">
              {['Kolom 1', 'Kolom 2', 'Kolom 3'].map((label, i) => (
                <div key={i} className="text-center">
                  <p className="text-stone-600 text-[10px] uppercase tracking-wider mb-2">{label}</p>
                  <div className="bg-[#171C14] rounded-lg p-3 border border-[#3A4530]">
                    <p style={{ fontFamily: "'JetBrains Mono', monospace" }}
                      className="text-[#F4F1E6] text-sm font-medium break-all leading-relaxed">
                      {i === 0 ? soal?.kolom1 : i === 1 ? soal?.kolom2 : soal?.kolom3}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            {[
              ['a', 'Semua kolom sama'],
              ['b', 'Kolom 1 & 2 sama'],
              ['c', 'Kolom 1 & 3 sama'],
              ['d', 'Kolom 2 & 3 sama'],
              ['e', 'Tidak ada yang sama'],
            ].map(([huruf, label]) => (
              <button
                key={huruf}
                onClick={() => handleJawab(huruf)}
                className="w-full flex items-center gap-3 bg-[#232A1C] border border-[#3A4530] rounded-xl px-4 py-3.5 text-left active:bg-[#2E3826] active:scale-[0.99] transition-all"
              >
                <span className="w-8 h-8 rounded-lg bg-[#2E3826] border border-[#3A4530] flex items-center justify-center text-[#C0A062] font-bold text-sm flex-shrink-0">
                  {huruf.toUpperCase()}
                </span>
                <span className="text-stone-300 text-sm">{label}</span>
                <ChevronRight size={16} className="text-stone-600 ml-auto flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>
      ) : jenisTampil === 'teks' ? (
        <div className="flex-1 px-4 py-6 flex flex-col justify-center">
          <p className="text-stone-500 text-xs uppercase tracking-wider mb-4 text-center">
            Apakah dua rangkaian berikut sama persis?
          </p>

          <div className="bg-[#232A1C] border border-[#3A4530] rounded-xl p-5 mb-6 space-y-3">
            {['A', 'B'].map((label, i) => (
              <div key={label}>
                <p className="text-stone-600 text-[10px] uppercase tracking-wider mb-1">Teks {label}</p>
                <div className="bg-[#171C14] rounded-lg p-3 border border-[#3A4530]">
                  <p style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    className="text-[#F4F1E6] text-sm font-medium break-words leading-relaxed">
                    {i === 0 ? soal?.teksA : soal?.teksB}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => handleJawab('sama')}
              className="flex items-center justify-center gap-2 bg-[#232A1C] border border-[#3A4530] rounded-xl px-4 py-4 active:bg-[#2E3826] active:scale-[0.99] transition-all"
            >
              <span className="w-8 h-8 rounded-full border-2 border-[#4ade80] flex items-center justify-center text-[#4ade80] font-bold text-sm flex-shrink-0">O</span>
              <span className="text-stone-300 text-sm font-semibold">SAMA</span>
            </button>
            <button
              onClick={() => handleJawab('beda')}
              className="flex items-center justify-center gap-2 bg-[#232A1C] border border-[#3A4530] rounded-xl px-4 py-4 active:bg-[#2E3826] active:scale-[0.99] transition-all"
            >
              <span className="w-8 h-8 rounded-full flex items-center justify-center text-[#f87171] font-bold text-sm flex-shrink-0">X</span>
              <span className="text-stone-300 text-sm font-semibold">BEDA</span>
            </button>
          </div>
        </div>
      ) : jenisTampil === 'hitungHuruf' ? (
        <div className="flex-1 px-4 py-6 flex flex-col justify-center">
          <p className="text-stone-500 text-xs uppercase tracking-wider mb-4 text-center">
            Berapa kali huruf "i" muncul di baris ini?
          </p>

          <div className="bg-[#232A1C] border border-[#3A4530] rounded-xl p-5 mb-6">
            <p style={{ fontFamily: "'JetBrains Mono', monospace" }}
              className="text-[#F4F1E6] text-xl sm:text-2xl font-bold text-center break-all leading-relaxed tracking-wider">
              {soal?.deret?.split('').map((ch, i) => (
                <span key={i} className={ch === 'i' ? 'text-[#C0A062]' : ''}>{ch}</span>
              ))}
            </p>
          </div>

          <div className="grid grid-cols-4 gap-2">
            {Array.from({ length: 8 }, (_, n) => n).map(n => (
              <button
                key={n}
                onClick={() => handleJawab(n)}
                className="flex items-center justify-center bg-[#232A1C] border border-[#3A4530] rounded-xl py-4 active:bg-[#2E3826] active:scale-[0.99] transition-all"
              >
                <span style={{ fontFamily: "'JetBrains Mono', monospace" }} className="text-[#F4F1E6] font-bold text-lg">{n}</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex-1 px-4 py-6 flex flex-col justify-center">
          <p className="text-stone-500 text-xs uppercase tracking-wider mb-4 text-center">
            Apakah dua gambar berikut sama persis?
          </p>

          <div className="bg-[#232A1C] border border-[#3A4530] rounded-xl p-5 mb-6">
            <div className="grid grid-cols-2 gap-4">
              {['A', 'B'].map((label, i) => (
                <div key={label} className="text-center">
                  <p className="text-stone-600 text-[10px] uppercase tracking-wider mb-2">Gambar {label}</p>
                  <div className="bg-[#171C14] rounded-lg p-2 border border-[#3A4530] aspect-square flex items-center justify-center">
                    <div
                      className="w-full h-full"
                      dangerouslySetInnerHTML={{ __html: i === 0 ? soal?.gambarA : soal?.gambarB }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => handleJawab('sama')}
              className="flex items-center justify-center gap-2 bg-[#232A1C] border border-[#3A4530] rounded-xl px-4 py-4 active:bg-[#2E3826] active:scale-[0.99] transition-all"
            >
              <span className="w-8 h-8 rounded-full border-2 border-[#4ade80] flex items-center justify-center text-[#4ade80] font-bold text-sm flex-shrink-0">O</span>
              <span className="text-stone-300 text-sm font-semibold">SAMA</span>
            </button>
            <button
              onClick={() => handleJawab('beda')}
              className="flex items-center justify-center gap-2 bg-[#232A1C] border border-[#3A4530] rounded-xl px-4 py-4 active:bg-[#2E3826] active:scale-[0.99] transition-all"
            >
              <span className="w-8 h-8 rounded-full flex items-center justify-center text-[#f87171] font-bold text-sm flex-shrink-0">X</span>
              <span className="text-stone-300 text-sm font-semibold">BEDA</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
